import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getStorageHealth } from "@/lib/audit/storageHealth.functions";
import { Database, HardDrive, Lock, Activity, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/admin/storage-health")({
  component: StorageHealthPage,
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">
      No se pudo cargar el panel: {error.message}
    </div>
  ),
  notFoundComponent: () => <div className="p-6 text-sm">No encontrado.</div>,
});

function StorageHealthPage() {
  const fn = useServerFn(getStorageHealth);
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["storage-health"],
    queryFn: () => fn(),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  const ro = data?.readOnly;
  const rec = data?.inRecovery;

  return (
    <main className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Storage & DB Health · Live</h1>
          <p className="text-sm text-muted-foreground">
            Tamaño, modo read-only y recovery — refresco automático cada 10s. Requiere superadmin.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-bold hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          Refrescar
        </button>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {error && (
        <p className="text-sm text-destructive">Error: {(error as Error).message}</p>
      )}

      {data && (
        <>
          {data.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {data.error}
            </div>
          )}

          <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <StatusTile
              icon={Lock}
              label="Read-only"
              value={ro ? "ACTIVO" : "off"}
              tone={ro ? "danger" : "ok"}
              hint={ro ? "default_transaction_read_only=on — la DB rechaza escrituras" : "La DB acepta escrituras"}
            />
            <StatusTile
              icon={Activity}
              label="Recovery"
              value={rec ? "EN RECOVERY" : "primario"}
              tone={rec ? "warn" : "ok"}
              hint={rec ? "pg_is_in_recovery()=true" : "Instancia primaria activa"}
            />
            <StatusTile
              icon={Database}
              label="Tamaño DB"
              value={data.dbSizePretty}
              tone="info"
              hint={`${data.dbSizeBytes.toLocaleString()} bytes`}
            />
          </section>

          <section className="rounded-xl border border-border bg-background/60 p-4">
            <div className="mb-3 flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-black">Top 10 tablas por tamaño total</h2>
            </div>
            <ul className="space-y-1">
              {data.topTables.map((t) => {
                const max = data.topTables[0]?.bytes || 1;
                const pct = Math.round((t.bytes / max) * 100);
                return (
                  <li key={t.name} className="space-y-0.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-mono">{t.name}</span>
                      <span className="font-bold tabular-nums">{t.pretty}</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded bg-secondary/40">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
              {data.topTables.length === 0 && (
                <li className="text-xs text-muted-foreground">Sin datos.</li>
              )}
            </ul>
          </section>

          <p className="text-[10px] text-muted-foreground">
            Última captura: {new Date(dataUpdatedAt).toLocaleTimeString()} · servidor: {new Date(data.collectedAt).toLocaleTimeString()}
          </p>
        </>
      )}
    </main>
  );
}

function StatusTile({
  icon: Icon,
  label,
  value,
  tone,
  hint,
}: {
  icon: typeof Database;
  label: string;
  value: string;
  tone: "ok" | "warn" | "danger" | "info";
  hint: string;
}) {
  const toneClass =
    tone === "danger"
      ? "border-red-500/40 bg-red-500/5"
      : tone === "warn"
      ? "border-amber-500/40 bg-amber-500/5"
      : tone === "ok"
      ? "border-emerald-500/30 bg-emerald-500/5"
      : "border-border bg-background/60";
  const Badge =
    tone === "danger" ? AlertTriangle : tone === "warn" ? AlertTriangle : tone === "ok" ? CheckCircle2 : Database;
  return (
    <div className={`rounded-xl border p-4 ${toneClass}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
        </div>
        <Badge className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-black tabular-nums">{value}</div>
      <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}